import React, { FC, SVGProps } from "react";
import { Box, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { SvgIconProps } from "@mui/material/SvgIcon/SvgIcon";
import WalletIcon from "@icons/myicons/wallet.svg?react";
import PiggyBankIcon from "@icons/myicons/piggy-bank.svg?react";
import InvestmentsIcon from "@icons/myicons/sidebar/investments.svg?react";
import CreditCardGroupIcon from "@icons/myicons/credit-card-hand-group.svg?react";
import HandshakeIcon from "@icons/myicons/handshake.svg?react";
import HomeIcon from "@icons/myicons/home-house-big.svg?react";
import ArrowsCircledIcon from "@icons/myicons/move-arrow-right-left-circle.svg?react";
import DollarIcon from "@icons/myicons/dollar-cash-circle.svg?react";
import { SettingsBankConnectionAccount } from "@features/settings/SettingsBankConnections/types";
import { toCurrency } from "@helpers";
import { Account } from "@api";

const ActiveBox = styled(Box)(({ theme }) => ({
  color: theme.palette.success.dark,
  backgroundColor: theme.palette.background.success,
  borderRadius: "6px",
  padding: theme.spacing(0.75, 2),
}));

const AccountIcon = styled(Box)(({ theme }) => ({
  width: "38px",
  height: "38px",
  borderRadius: "50%",
  backgroundColor: theme.palette.border.secondary,
  color: theme.palette.grey[600],
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  "& svg": {
    width: "18px",
    height: "18px",
  },
}));

const iconMap: Record<
  SettingsBankConnectionAccount["type"],
  FC<SVGProps<SVGSVGElement>> | FC<SvgIconProps>
> = {
  1: WalletIcon,
  2: PiggyBankIcon,
  3: CreditCardIcon,
  4: InvestmentsIcon,
  5: CreditCardGroupIcon,
  6: WalletIcon,
  7: HandshakeIcon,
  8: HomeIcon,
  21: ArrowsCircledIcon,
  22: DollarIcon,
  "01_depot": InvestmentsIcon,
  "02_cash": WalletIcon,
};

type Props = {
  accounts?: Account[];
  account: SettingsBankConnectionAccount;
  provider?: "WEALTH_API" | "FIN_API";
};

export const AccountRow = ({ account, provider, accounts }: Props) => {
  const { t } = useTranslation();
  const Icon = iconMap[account.type] || null;
  const importedAccount =
    provider === "FIN_API"
      ? accounts?.find((acc) => acc.finApiId === account.id)
      : null;

  return (
    <Box display="flex" justifyContent="space-between" py={4}>
      <Box display="flex" alignItems="center" gap={3}>
        <Box>
          <AccountIcon>
            <Icon width="18px" height="18px" />
          </AccountIcon>
        </Box>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body1" color="textSecondary">
              {t(`settings.bankConnection.accountTypes.${account.type}`)}
            </Typography>
            <Typography variant="body1">{account.name}</Typography>
            {Boolean(importedAccount) && provider === "FIN_API" && (
              <ActiveBox color="success.main">Active</ActiveBox>
            )}
          </Box>
          <Typography variant="body3" color="textSecondary">
            {[account.iban, importedAccount?.id]
              .filter(Boolean)
              .join(" • ID: ")}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={6}>
        <Typography variant="body1">
          {toCurrency(account.balance, account.currency)}
        </Typography>
      </Box>
    </Box>
  );
};
